import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { PlayerProfileScopeSwitcher } from "../components/PlayerFamilyProfileSwitcher";
import { makeLatestOnly } from "../lib/latestOnly";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";
import EliminationBracket from "../components/EliminationBracket";
import PlayerRrPosterTable from "../components/PlayerRrPosterTable";
import PlayerStageLockedCard from "../components/PlayerStageLockedCard";
import PlayerTournamentBottomNav from "../components/PlayerTournamentBottomNav";
import { playerStageLocked } from "../lib/playerStage";
import { SCHEDULE_PENDING_LABEL } from "../lib/matchScheduleStatus";
import TournamentSponsorSlots from "../components/TournamentSponsorSlots";
import { isPastCdmx } from "../components/DrawCeremonyCard";
import {
  api,
  ApiError,
  matchParticipantShortName,
  type DrawCeremony,
  type PlayerStageVisibility,
  type StandingsGroup,
  type TournamentMatch,
  type JornadaWindow,
} from "../lib/api";
import { cdmxDayMonthEs, formatDateTime } from "../lib/dateFormat";
import {
  buildPlayerDrawSections,
  divisionKeyFor,
  sortedGroupParticipants,
  type DrawDivisionBrief,
  type PlayerDrawGroup,
  type PlayerDrawSection,
} from "../lib/playerDrawSections";
import {
  buildRolDeJuegos,
  capitalized,
  deriveMyGroupSummary,
  deriveMyPath,
  isTieMatch,
  jornadaNumber,
  participantName,
  qualifyText,
  rolWhenText,
  scoreFromSide,
  tiebreakLegendText,
  visibleRolWindow,
  type MyPath,
  type PlayerWhen,
} from "../lib/playerDrawMine";
import { openPrintWindow } from "../lib/printTemplate";
import { jornadaRangeShort, jornadaRangeWithYear, windowForMatch } from "../lib/jornadaDates";
import { buildDrawPosterHtml, openDrawPosterWindow, type PosterStanding } from "../lib/drawPoster";
import { dropMatchState } from "../lib/dropMatch";
import { fetchPlayerBranding } from "../lib/playerBranding";
import { playerBannerList } from "../lib/tournamentExtras";
import { absoluteAssetUrl } from "../lib/rulesPresentation";
import { API_BASE_URL } from "../lib/api";
import { isConsolationLabel } from "../lib/bracketFromMatches";
import { eliminationRoundDisplayLabel } from "../lib/roundRobinPhases";
import "./playerConsolation.css";

/**
 * Draw del jugador (maquetas aprobadas 2026-10-16: Jugador-Draw-RR-cel,
 * Jugador-Draw-Elim-cel y Jugador-Draw). Celular primero:
 * - Round robin: «Mi grupo» (lugar, ganados, games, próximo partido y mis
 *   resultados), posiciones del grupo con «Tú», tabla de cruces como el
 *   póster (nombres fijos al deslizar), leyenda con el desempate, rol de
 *   juegos por jornada, otros grupos plegados y lugar de los playoffs.
 * - Eliminatoria: «Mi camino» y el cuadro con tu camino en lima.
 * Todo sale de los partidos que el jugador ya recibe y de la tabla de
 * posiciones que ya ve; los horarios sin publicar dicen «Horario por confirmar».
 */

/** «Jornada 3» → «J3». */
export function jornadaShort(roundLabel: string | null | undefined): string {
  const found = /(\d+)/.exec(roundLabel ?? "");
  if (found) return `J${Number(found[1])}`;
  return roundLabel?.trim() || "Por jugar";
}

/** Fechas de la jornada para la celda sin calendario del póster. */
function posterWindow(windows: JornadaWindow[], match: TournamentMatch): { jornadaRange: string | null; jornadaShort: string | null } {
  const window = windowForMatch(windows, match);
  return {
    jornadaRange: window ? jornadaRangeWithYear(window.starts_on, window.ends_on) : null,
    jornadaShort: window ? jornadaRangeShort(window.starts_on, window.ends_on) : null,
  };
}

function useIsWide(): boolean {
  const query = "(min-width: 1024px)";
  const [wide, setWide] = useState(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia(query);
    const update = () => setWide(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);
  return wide;
}

function WhenPills({ when }: { when: PlayerWhen }) {
  if (when.pending) {
    return (
      <div className="pdraw-pills">
        <span className="pdraw-pill">{SCHEDULE_PENDING_LABEL}</span>
      </div>
    );
  }
  const parts = [capitalized(when.day), when.time, when.court].filter(Boolean);
  if (parts.length === 0) {
    return (
      <div className="pdraw-pills">
        <span className="pdraw-pill">Por programar</span>
      </div>
    );
  }
  return (
    <div className="pdraw-pills">
      {parts.map((part) => (
        <span key={part} className="pdraw-pill">
          {part}
        </span>
      ))}
    </div>
  );
}

/** Serie por equipos: un partido con sus líneas (2 singles y 1 dobles). */
function TieLines({ match, mySide }: { match: TournamentMatch; mySide: number | null }) {
  const lines = match.lines ?? [];
  if (lines.length === 0) return null;
  return (
    <ul className="pdraw-lines">
      {lines.map((line, index) => {
        const kind = line.line_kind === "doubles" ? "Dobles" : line.line_kind === "mixed" ? "Mixto" : `Single ${index + 1}`;
        const names = (side: "team1" | "team2") => (line.line_players?.[side] ?? []).map((player) => player.name).join(" / ");
        const left = names("team1") || participantName(match.player1);
        const right = names("team2") || participantName(match.player2);
        const score = line.winner_participant_id != null ? scoreFromSide(line, line.player1_participant_id ?? 0) : null;
        const mine = mySide != null && (line.player1_participant_id === mySide || line.player2_participant_id === mySide);
        return (
          <li key={line.id} className={mine ? "is-mine" : undefined}>
            <strong>{kind}</strong>
            <span>
              {left} vs {right}
            </span>
            <small>{score ?? (line.winner_participant_id != null ? "Jugado" : "Por jugar")}</small>
          </li>
        );
      })}
    </ul>
  );
}

function consolationDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  const text = date.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" });
  return text.charAt(0).toUpperCase() + text.slice(1).replace(/\./g, "");
}

export default function PlayerTournamentBracketPage() {
  const { id } = useParams();
  const tournamentId = Number(id);
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cuadro principal / Consolación (maqueta Formato-Consolacion 2026-10-17).
  const [bracketTab, setBracketTab] = useState<"main" | "consolation">("main");
  const [tournament, setTournament] = useState<{ id: number; name: string } | null>(null);
  const [visibility, setVisibility] = useState<PlayerStageVisibility | null>(null);
  const [matches, setMatches] = useState<TournamentMatch[]>([]);
  const [divisions, setDivisions] = useState<DrawDivisionBrief[]>([]);
  const [tournamentFormatType, setTournamentFormatType] = useState<string | null>(null);
  const [myParticipantId, setMyParticipantId] = useState<number | null>(null);
  const [drawCeremonies, setDrawCeremonies] = useState<DrawCeremony[]>([]);
  const [roundWindows, setRoundWindows] = useState<JornadaWindow[]>([]);
  // Orden de la hoja de los grupos acomodados por el organizador (Eduardo
  // 2026-10-01): la tabla del grupo sigue ese orden; sin él, el de siempre.
  const [groupOrders, setGroupOrders] = useState<Record<string, number[]>>({});
  // Tabla de posiciones que el jugador ya ve en «Ranking»: G-P, games y lugar.
  const [standingsGroups, setStandingsGroups] = useState<StandingsGroup[]>([]);
  // Cuenta familiar (Eduardo 2026-09-29): «Tú», tu grupo y tu partido son
  // los del perfil elegido para este torneo.
  const scope = usePlayerProfileScope({ tournamentId });
  const scopeProfileId = scope.profileId;
  const latestLoad = useMemo(() => makeLatestOnly(), []);
  // Categoría elegida en los chips (null = la tuya) y grupos abiertos/cerrados a mano.
  const [pickedSectionKey, setPickedSectionKey] = useState<string | null>(null);
  const [groupOpenOverrides, setGroupOpenOverrides] = useState<Record<string, boolean>>({});
  const [showAllRol, setShowAllRol] = useState(false);
  const isWide = useIsWide();

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId) || !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    try {
      const [response, standingsResponse] = await Promise.all([
        api.resultReports.listTournamentResultsForPlayer(token, tournamentId, scopeProfileId),
        // La tabla es un extra: si no llega, la página sigue con «—».
        Promise.resolve()
          .then(() => api.standings.forPlayer(token, tournamentId, scopeProfileId))
          .catch(() => null),
      ]);
      if (!latestLoad.isCurrent(attempt)) return;
      setTournament(response.data.tournament);
      setVisibility(response.data.player_visibility ?? null);
      setMatches(response.data.matches);
      setDivisions(response.data.divisions ?? []);
      setTournamentFormatType(response.data.format_type ?? null);
      setMyParticipantId(response.data.my_participant_id);
      setDrawCeremonies(response.data.draw_ceremonies ?? []);
      setRoundWindows(response.data.round_windows ?? []);
      setGroupOrders(response.data.group_orders ?? {});
      setStandingsGroups(standingsResponse?.data?.groups ?? []);
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el bracket del torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [latestLoad, scope.ready, scopeProfileId, token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sections = useMemo<PlayerDrawSection[]>(
    () => buildPlayerDrawSections(matches, { divisions, tournamentFormatType, myParticipantId, groupOrders }),
    [divisions, groupOrders, matches, myParticipantId, tournamentFormatType],
  );

  // La categoría del jugador: cualquier partido suyo trae su division_id.
  const myDivisionKey = useMemo<string | null>(() => {
    if (!myParticipantId) return null;
    const own = matches.find(
      (match) =>
        match.player1_participant_id === myParticipantId || match.player2_participant_id === myParticipantId,
    );
    return own ? divisionKeyFor(own) : null;
  }, [matches, myParticipantId]);

  const standingsByGroupId = useMemo(() => {
    const map = new Map<string, StandingsGroup>();
    for (const group of standingsGroups) {
      if (group.group_id != null) map.set(String(group.group_id), group);
    }
    return map;
  }, [standingsGroups]);

  function printBracket(section: PlayerDrawSection) {
    const node = document.getElementById(`player-bracket-sheet-${section.key}`);
    if (!node) return;
    try {
      openPrintWindow({
        title: `Bracket ${section.name} · ${tournament?.name ?? "Torneos123"}`,
        eyebrow: "Bracket del torneo",
        heading: tournament?.name ?? "Torneos123",
        subtitle: sections.length > 1 ? section.name : undefined,
        bodyHtml: node.outerHTML,
        landscape: true,
        fitToPage: true,
        includeAppStyles: true,
        extraCss: `
          .player-bracket-sheet { margin: 0 !important; padding: 0 !important; border: 0 !important; box-shadow: none !important; overflow: visible !important; }
        `,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo abrir la impresión.");
    }
  }

  /** Póster del grupo (el mismo que pega el club), con lo que el jugador ve. */
  async function downloadPoster(section: PlayerDrawSection, group: PlayerDrawGroup) {
    let target: ReturnType<typeof openDrawPosterWindow>;
    try {
      target = openDrawPosterWindow();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo abrir la ventana del póster.");
      return;
    }
    try {
      const branding = token ? await fetchPlayerBranding(token, tournamentId).catch(() => null) : null;
      const asset = (url: string | null | undefined) => absoluteAssetUrl(url, API_BASE_URL, window.location.origin);
      const standing = standingsByGroupId.get(group.key) ?? null;
      const participants = sortedGroupParticipants(group);
      target.write(
        buildDrawPosterHtml({
          tournamentName: tournament?.name ?? "Torneos123",
          categoryName: section.name,
          formatLabel: "Round Robin · todos contra todos",
          qualifyText: qualifyText(standing?.qualify_count),
          organizerLogoUrl: asset(branding?.data.organizer_logo_url),
          tournamentLogoUrl: asset(branding?.data.logo_url),
          sponsorLogoUrls: branding ? playerBannerList(branding.data).map((banner) => asset(banner.image_url)) : [],
          groups: [
            {
              name: group.name,
              players: participants.map((participant) => ({
                id: participant.id,
                name: participant.display_name?.trim() || matchParticipantShortName(participant),
                dropped: participant.current_status === "dropped",
              })),
              standings: new Map<number, PosterStanding>(
                (standing?.standings ?? []).map((row) => [
                  row.participant_id,
                  { wins: row.wins, losses: row.losses, gamesWon: row.games_won, gamesLost: row.games_lost },
                ]),
              ),
              matches: [...group.matchesByPair.values()].flatMap((match) =>
                match.player1_participant_id && match.player2_participant_id
                  ? [
                      {
                        id: match.id,
                        roundNumber: jornadaNumber(match.round_label),
                        status: String(match.status),
                        // El servidor ya vació el horario sin publicar.
                        scheduledDate: match.schedule_pending ? null : match.scheduled_date,
                        scheduledTime: match.schedule_pending ? null : match.scheduled_time,
                        court: match.schedule_pending ? null : match.court,
                        player1Id: match.player1_participant_id,
                        player2Id: match.player2_participant_id,
                        winnerId: match.winner_participant_id ?? null,
                        scoreText: match.score_text,
                        drop: isTieMatch(match) ? null : dropMatchState(match),
                        ...posterWindow(roundWindows, match),
                      },
                    ]
                  : [],
              ),
            },
          ],
        }),
      );
    } catch {
      target.fail("No pudimos armar el póster. Intenta otra vez.");
    }
  }

  if (Number.isNaN(tournamentId)) {
    return <Navigate to="/player/tournaments" replace />;
  }

  const openSectionKey = myDivisionKey && sections.some((section) => section.key === myDivisionKey)
    ? myDivisionKey
    : sections[0]?.key ?? null;
  // Selector de categoría (maqueta 2026-10-06): tu categoría primero.
  const selectedSection =
    sections.find((section) => section.key === (pickedSectionKey ?? openSectionKey)) ?? sections[0] ?? null;

  const myGroup =
    selectedSection && myParticipantId != null
      ? selectedSection.groups.find((group) => group.participantsById.has(myParticipantId)) ?? null
      : null;
  const focusGroup = myGroup ?? selectedSection?.groups[0] ?? null;
  const focusStandings = focusGroup ? standingsByGroupId.get(focusGroup.key) ?? null : null;
  const sectionMatches = selectedSection ? matches.filter((match) => divisionKeyFor(match) === selectedSection.key) : [];
  const myPath: MyPath | null =
    selectedSection && myParticipantId != null && selectedSection.bracket.rounds.length > 0
      ? deriveMyPath({
          rounds: selectedSection.bracket.rounds,
          matches: sectionMatches.filter((match) => !isConsolationLabel(match.round_label)),
          myParticipantId,
        })
      : null;

  function groupMatches(group: PlayerDrawGroup): TournamentMatch[] {
    return sectionMatches.filter((match) => String(match.group_id ?? "") === group.key);
  }

  function sectionSubtitle(section: PlayerDrawSection): string {
    if (section.groups.length > 0) {
      const size = Math.max(...section.groups.map((group) => group.participantsById.size));
      const rounds = new Set(
        sectionMatches.filter((match) => match.group_id != null).map((match) => /(\d+)/.exec(match.round_label ?? "")?.[1] ?? ""),
      );
      rounds.delete("");
      const parts = [
        section.groups.length > 1 ? `Grupos de ${size}` : `Grupo de ${size}`,
        "todos contra todos",
        rounds.size > 0 ? `${rounds.size} jornada${rounds.size === 1 ? "" : "s"}` : null,
      ];
      return parts.filter(Boolean).join(" · ");
    }
    if (section.isLadder) return "Escalera · cuadro de playoffs";
    const firstRound = section.bracket.rounds[0];
    const size = firstRound ? firstRound.matches.length * 2 : 0;
    return `Eliminatoria de ${size}${section.bracket.thirdPlace ? " · con partido por 3er lugar" : ""}`;
  }

  function groupIsOpen(group: PlayerDrawGroup): boolean {
    return groupOpenOverrides[group.key] ?? false;
  }

  // ---- Mi grupo ----------------------------------------------------------
  function renderMyGroupCard(section: PlayerDrawSection, group: PlayerDrawGroup) {
    if (myParticipantId == null) return null;
    const participants = sortedGroupParticipants(group);
    const standings = standingsByGroupId.get(group.key) ?? null;
    const summary = deriveMyGroupSummary({
      matches: groupMatches(group),
      participantIds: participants.map((participant) => participant.id),
      myParticipantId,
      standings,
    });
    const pass = qualifyText(standings?.qualify_count);
    return (
      <section className="pdraw-mine" aria-label="Mi grupo">
        <div className="pdraw-mine__head">
          <div>
            <span className="pdraw-mine__eyebrow">Mi grupo · {section.name}</span>
            <strong className="pdraw-mine__title">{group.name}</strong>
          </div>
          {pass ? <span className="pdraw-mine__pass">{pass}</span> : null}
        </div>
        <div className="pdraw-mine__grid">
          <div className="pdraw-mine__main">
            <div className="pdraw-mine__stats">
              <div>
                <span>Tu lugar</span>
                <strong>{summary.position != null ? `${summary.position}º de ${summary.total}` : "—"}</strong>
              </div>
              <div>
                <span>Ganados</span>
                <strong>{summary.wins != null ? `${summary.wins}-${summary.losses}` : "—"}</strong>
              </div>
              <div>
                <span>Games</span>
                <strong>{summary.gamesWon != null ? `${summary.gamesWon}-${summary.gamesLost}` : "—"}</strong>
              </div>
            </div>
            {summary.next ? (
              <div className="pdraw-next">
                <span className="pdraw-next__eyebrow">
                  Tu próximo partido{summary.next.jornada != null ? ` · Jornada ${summary.next.jornada}` : ""}
                </span>
                <div className="pdraw-next__who">
                  {summary.next.opponentNumber != null ? <span className="pdraw-num">{summary.next.opponentNumber}</span> : null}
                  <strong>
                    {isTieMatch(summary.next.match) ? "Serie vs " : "vs "}
                    {participantName(summary.next.opponent)}
                  </strong>
                </div>
                <WhenPills when={summary.next.when} />
                {isTieMatch(summary.next.match) ? <TieLines match={summary.next.match} mySide={null} /> : null}
              </div>
            ) : (
              <div className="pdraw-next pdraw-next--done">
                <span className="pdraw-next__eyebrow">Tu próximo partido</span>
                <strong>Ya jugaste todos tus partidos del grupo.</strong>
              </div>
            )}
          </div>
          <div className="pdraw-mine__results">
            <span className="pdraw-mine__eyebrow">Mis resultados</span>
            {summary.results.length === 0 ? (
              <p className="pdraw-mine__empty">Aquí verás tus marcadores en cuanto juegues.</p>
            ) : (
              <ul className="pdraw-results">
                {summary.results.map((result) => (
                  <li key={result.match.id}>
                    {result.opponentNumber != null ? <span className="pdraw-num pdraw-num--soft">{result.opponentNumber}</span> : null}
                    <div className="pdraw-results__who">
                      <strong>{participantName(result.opponent)}</strong>
                      <small>
                        {[result.jornada != null ? `Jornada ${result.jornada}` : null, result.day || null].filter(Boolean).join(" · ")}
                      </small>
                    </div>
                    <div className="pdraw-results__score">
                      <span className={`pdraw-verdict${result.won ? " is-won" : " is-lost"}`}>{result.won ? "Ganaste" : "Perdiste"}</span>
                      <small>{result.score ?? ""}</small>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
    );
  }

  // ---- Mi camino (eliminatoria) -----------------------------------------
  function renderMyPathCard(section: PlayerDrawSection, path: MyPath) {
    const next = path.steps.find((step) => step.state === "next") ?? null;
    const title = path.alive
      ? `Sigues vivo${path.currentLabel ? ` · ${path.currentLabel}` : ""}`
      : `Quedaste en ${path.currentLabel ?? "el cuadro"}`;
    return (
      <section className="pdraw-mine" aria-label="Mi camino">
        <div className="pdraw-mine__head">
          <div>
            <span className="pdraw-mine__eyebrow">Mi camino · {section.name}</span>
            <strong className="pdraw-mine__title">{title}</strong>
          </div>
          <span className="pdraw-mine__pass">
            {path.wins} ganado{path.wins === 1 ? "" : "s"}
          </span>
        </div>
        {next ? (
          <div className="pdraw-next">
            <span className="pdraw-next__eyebrow">Tu próximo partido · {next.label}</span>
            <div className="pdraw-next__who">
              <strong>
                {next.match && isTieMatch(next.match) ? "Serie vs " : "vs "}
                {next.opponent ? participantName(next.opponent) : "Por definir"}
              </strong>
            </div>
            {next.when ? <WhenPills when={next.when} /> : null}
            {next.match && isTieMatch(next.match) ? <TieLines match={next.match} mySide={null} /> : null}
          </div>
        ) : null}
        <ol className="pdraw-path">
          {path.steps.map((step, index) => {
            const whenText = step.when
              ? step.when.pending
                ? SCHEDULE_PENDING_LABEL
                : [capitalized(step.when.day), step.when.time, step.when.court].filter(Boolean).join(" · ")
              : "";
            const detail =
              step.state === "won"
                ? `Ganaste ${step.score ?? ""} a ${participantName(step.opponent)}`
                : step.state === "lost"
                  ? `Perdiste ${step.score ?? ""} con ${participantName(step.opponent)}`
                  : step.state === "next"
                    ? [whenText, step.opponent ? `vs ${participantName(step.opponent)}` : null].filter(Boolean).join(" · ")
                    : whenText || "Por definir";
            return (
              <li key={`${step.label}-${index}`} className={`pdraw-path__step is-${step.state}`}>
                <span className="pdraw-path__dot" aria-hidden="true">
                  {step.state === "won" ? "✓" : step.state === "lost" ? "✕" : index + 1}
                </span>
                <div>
                  <strong>{step.label}</strong>
                  <span>{detail.replace(/\s+/g, " ").trim()}</span>
                </div>
              </li>
            );
          })}
        </ol>
      </section>
    );
  }

  // ---- Posiciones (celular) -------------------------------------------
  function renderStandingsList(group: PlayerDrawGroup, standings: StandingsGroup | null) {
    if (!standings || standings.standings.length === 0) return null;
    const participants = sortedGroupParticipants(group);
    const numberById = new Map(participants.map((participant, index) => [participant.id, index + 1]));
    const anyPlayed = standings.standings.some((row) => row.matches_played > 0);
    const rows = [...standings.standings].sort((a, b) => a.position - b.position);
    return (
      <section className="ps-card pdraw-standings pdraw-only-phone" aria-label={`Posiciones · ${group.name}`}>
        <div className="pdraw-card-head">
          <strong>Posiciones · {group.name}</strong>
          <span>{participants.length} jugadores</span>
        </div>
        <div className="pdraw-standings__labels" aria-hidden="true">
          <span>Lugar</span>
          <span />
          <span>G-P</span>
          <span>Games</span>
        </div>
        <ol className="pdraw-standings__list">
          {rows.map((row) => {
            const isMe = row.participant_id === myParticipantId;
            return (
              <li key={row.participant_id} className={`pdraw-standings__row${isMe ? " is-me" : ""}`}>
                <span className="pdraw-standings__place">{anyPlayed ? `${row.position}º` : "—"}</span>
                <span className="pdraw-standings__who">
                  {numberById.has(row.participant_id) ? <span className="pdraw-num">{numberById.get(row.participant_id)}</span> : null}
                  <span>
                    <strong>{row.short_name?.trim() || row.display_name}</strong>
                    {isMe ? <span className="pdraw-tu">Tú</span> : null}
                  </span>
                </span>
                <span className="pdraw-standings__gp">
                  {row.wins}-{row.losses}
                </span>
                <span className="pdraw-standings__games">
                  {row.games_won}-{row.games_lost}
                </span>
              </li>
            );
          })}
        </ol>
      </section>
    );
  }

  // ---- Rol de juegos ---------------------------------------------------
  function renderRol(group: PlayerDrawGroup) {
    const rounds = buildRolDeJuegos(groupMatches(group));
    if (rounds.length === 0) return null;
    const participants = sortedGroupParticipants(group);
    const numberById = new Map(participants.map((participant, index) => [participant.id, index + 1]));
    const windowSize = isWide ? 4 : 2;
    const visible = showAllRol ? rounds : visibleRolWindow(rounds, windowSize);
    const hidden = rounds.length - visible.length;
    const side = (match: TournamentMatch, which: 1 | 2) => {
      const pid = which === 1 ? match.player1_participant_id : match.player2_participant_id;
      const brief = which === 1 ? match.player1 : match.player2;
      const won = match.winner_participant_id != null && match.winner_participant_id === pid;
      return (
        <span className={`pdraw-rol__side${won ? " is-winner" : ""}${pid === myParticipantId ? " is-me" : ""}`}>
          <span className="pdraw-num pdraw-num--xs">{pid != null ? numberById.get(pid) ?? "·" : "·"}</span>
          <span className="pdraw-rol__name">{participantName(brief)}</span>
        </span>
      );
    };
    return (
      <section className="ps-card pdraw-rol" aria-label={`Rol de juegos · ${group.name}`}>
        <div className="pdraw-card-head">
          <strong>Rol de juegos</strong>
          <span>por jornada{myParticipantId != null ? " · tus partidos en lima" : ""}</span>
        </div>
        <div className="pdraw-rol__grid">
          {visible.map((round) => (
            <div key={round.round} className={`pdraw-rol__col${round.isNext ? " is-next" : ""}`}>
              <div className="pdraw-rol__head">
                <strong>
                  {round.round > 0 ? `Jornada ${round.round}` : "Sin jornada"}
                  {round.day ? ` · ${round.day}` : ""}
                </strong>
                {round.isNext ? (
                  <span className="pdraw-rol__tag is-next">Próxima</span>
                ) : round.played ? (
                  <span className="pdraw-rol__tag">Jugada</span>
                ) : null}
              </div>
              {round.matches.map((match) => {
                const mine =
                  myParticipantId != null &&
                  (match.player1_participant_id === myParticipantId || match.player2_participant_id === myParticipantId);
                const drop = isTieMatch(match) ? null : dropMatchState(match);
                const decided = match.winner_participant_id != null;
                const winnerSide = decided ? match.winner_participant_id ?? 0 : 0;
                const right = decided
                  ? scoreFromSide(match, winnerSide) ?? "Jugado"
                  : drop && drop.kind !== "pending"
                    ? drop.kind === "cancelled"
                      ? "Cancelado"
                      : "W.O."
                    : rolWhenText(match);
                return (
                  <div key={match.id} className={`pdraw-rol__match${mine ? " is-mine" : ""}`}>
                    <div className="pdraw-rol__sides">
                      {side(match, 1)}
                      {side(match, 2)}
                    </div>
                    <span className={`pdraw-rol__right${decided ? " is-score" : ""}`}>{right}</span>
                    {isTieMatch(match) ? <TieLines match={match} mySide={null} /> : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {rounds.length > visible.length || showAllRol ? (
          <div className="pdraw-rol__more">
            <button type="button" className="pdraw-btn" onClick={() => setShowAllRol((value) => !value)}>
              {showAllRol ? "Ver menos jornadas" : hidden > 0 ? `Ver las ${rounds.length} jornadas` : "Ver menos jornadas"}
            </button>
          </div>
        ) : null}
      </section>
    );
  }

  function renderCrossCard(group: PlayerDrawGroup, standings: StandingsGroup | null, titled: boolean) {
    return (
      <section className="ps-card pdraw-cross-card" aria-label={`Tabla de cruces · ${group.name}`}>
        {titled ? (
          <div className="pdraw-card-head">
            <strong>
              <span className="pdraw-only-wide">{group.name} · tabla de cruces</span>
              <span className="pdraw-only-phone">Tabla de cruces</span>
            </strong>
            <span>
              <span className="pdraw-only-phone">Desliza →</span>
              <span className="pdraw-only-wide">Igual que el póster del club · tu fila resaltada</span>
            </span>
          </div>
        ) : null}
        {titled ? (
          <p className="ps-hint pdraw-only-phone">Igual que el póster del club. Los nombres se quedan fijos al deslizar.</p>
        ) : null}
        <PlayerRrPosterTable
          group={group}
          myParticipantId={myParticipantId}
          standings={standings}
          qualifyCount={standings?.qualify_count ?? null}
        />
      </section>
    );
  }

  function renderLegend(standings: StandingsGroup | null) {
    const pass = standings?.qualify_count ?? null;
    return (
      <section className="ps-card pdraw-legend" aria-label="Leyenda">
        <strong className="pdraw-legend__title">Leyenda</strong>
        <div className="pdraw-legend__items">
          <span>
            <i className="pdraw-sw pdraw-sw--won" />
            Ganó (marcador de quien está en la fila)
          </span>
          <span>
            <i className="pdraw-sw pdraw-sw--lost" />
            Perdió
          </span>
          <span>
            <i className="pdraw-sw pdraw-sw--pending" />
            Por jugar: jornada, día, hora y cancha
          </span>
          <span>
            <i className="pdraw-sw pdraw-sw--self" />
            Mismo jugador
          </span>
          {pass ? (
            <span>
              <i className="pdraw-sw pdraw-sw--pass" />
              Lugares que pasan a playoffs ({pass === 1 ? "1º" : pass === 2 ? "1º y 2º" : `1º al ${pass}º`})
            </span>
          ) : null}
        </div>
        <p className="pdraw-legend__rule">{tiebreakLegendText(standings?.tiebreakers ?? null)}</p>
      </section>
    );
  }

  function renderPlayoffsPlaceholder(section: PlayerDrawSection) {
    const count = focusStandings?.qualify_count ?? null;
    const twoByTwo = section.groups.length === 2 && count === 2;
    const [a, b] = section.groups;
    return (
      <section className="ps-card ps-card--dashed pdraw-playoffs" aria-label="Playoffs">
        <div className="pdraw-card-head">
          <strong>Playoffs</strong>
          <span className="pdraw-rol__tag">Por definir</span>
        </div>
        <p className="ps-muted">
          Aparecen aquí, arriba de los grupos, en cuanto el organizador los genere.
          {count ? ` ${count === 1 ? "Pasa el 1º" : count === 2 ? "Pasan el 1º y 2º" : `Pasan del 1º al ${count}º`} de cada grupo.` : ""}
        </p>
        {twoByTwo && a && b ? (
          <div className="pdraw-playoffs__grid">
            <div>
              <span>Semifinal 1</span>
              <small>1º {a.name}</small>
              <small>2º {b.name}</small>
            </div>
            <div>
              <span>Semifinal 2</span>
              <small>1º {b.name}</small>
              <small>2º {a.name}</small>
            </div>
            <div>
              <span>Final</span>
              <small>Ganador SF1</small>
              <small>Ganador SF2</small>
            </div>
          </div>
        ) : null}
      </section>
    );
  }

  function renderBracketCard(section: PlayerDrawSection, path: MyPath | null, title: string) {
    const hasConsolation = section.consolation.rounds.length > 0;
    const showConsolation = hasConsolation && bracketTab === "consolation";
    const myConsolationIds =
      myParticipantId == null
        ? []
        : sectionMatches
            .filter(
              (match) =>
                isConsolationLabel(match.round_label)
                && (match.player1_participant_id === myParticipantId || match.player2_participant_id === myParticipantId),
            )
            .map((match) => match.id);
    return (
      <section className="ps-card pdraw-bracket" aria-label={title}>
        <div className="pdraw-card-head">
          <strong>{showConsolation ? "Consolación" : title}</strong>
          <span>Desliza a la derecha para ver las rondas finales.</span>
        </div>
        {hasConsolation ? (
          <div className="ps-chips pdraw-cons-tabs" role="group" aria-label="Cuadro">
            <button
              type="button"
              className={`ps-chip-button${showConsolation ? "" : " is-active"}`}
              aria-pressed={!showConsolation}
              onClick={() => setBracketTab("main")}
            >
              Cuadro principal
            </button>
            <button
              type="button"
              className={`ps-chip-button${showConsolation ? " is-active" : ""}`}
              aria-pressed={showConsolation}
              onClick={() => setBracketTab("consolation")}
            >
              Consolación
            </button>
          </div>
        ) : null}
        <div id={`player-bracket-sheet-${section.key}`} className="player-bracket-sheet">
          {showConsolation ? (
            <EliminationBracket
              rounds={section.consolation.rounds}
              thirdPlace={section.consolation.thirdPlace}
              pathMatchIds={myConsolationIds.length > 0 ? new Set(myConsolationIds) : null}
            />
          ) : (
            <EliminationBracket
              rounds={section.bracket.rounds}
              thirdPlace={section.bracket.thirdPlace}
              pathMatchIds={path?.matchIds ?? null}
            />
          )}
        </div>
        {path && !showConsolation ? <p className="ps-hint">Tu camino va marcado en lima en todas las rondas.</p> : null}
        {showConsolation && myConsolationIds.length > 0 ? (
          <p className="ps-hint">Tus partidos de consolación van marcados en lima.</p>
        ) : null}
      </section>
    );
  }

  /** «Sigues jugando · Consolación»: el jugador perdió en el cuadro principal
   * y tiene un partido de consolación pendiente. */
  function renderConsolationCard(): JSX.Element | null {
    if (myParticipantId == null) return null;
    const isMine = (match: TournamentMatch) =>
      match.player1_participant_id === myParticipantId || match.player2_participant_id === myParticipantId;
    const next = sectionMatches
      .filter(
        (match) =>
          isConsolationLabel(match.round_label)
          && isMine(match)
          && match.winner_participant_id == null
          && String(match.status) !== "cancelled",
      )
      .sort((a, b) => a.id - b.id)[0];
    if (!next) return null;
    const lost = sectionMatches.find(
      (match) =>
        !isConsolationLabel(match.round_label)
        && match.group_id == null
        && isMine(match)
        && match.winner_participant_id != null
        && match.winner_participant_id !== myParticipantId,
    );
    const lostRival = lost ? (lost.player1_participant_id === myParticipantId ? lost.player2 : lost.player1) : null;
    const rival = next.player1_participant_id === myParticipantId ? next.player2 : next.player1;
    const when = next.schedule_pending
      ? null
      : [
          consolationDate(next.scheduled_date),
          next.scheduled_time?.slice(0, 5),
          next.court ? `Cancha ${next.court.replace(/^cancha\s*/i, "")}` : null,
        ]
          .filter(Boolean)
          .join(" · ");
    return (
      <section className="ps-card pdraw-cons-card" aria-label="Sigues jugando en consolación">
        {lost ? (
          <p className="pdraw-cons-card__lost">
            Perdiste en {eliminationRoundDisplayLabel(lost.round_label)}
            {lostRival ? ` vs ${matchParticipantShortName(lostRival)}` : ""}
            {lost.score_text ? ` (${lost.player2_participant_id === myParticipantId ? lost.score_text.replace(/(\d+)-(\d+)/g, "$2-$1") : lost.score_text})` : ""}.
          </p>
        ) : null}
        <span className="pdraw-cons-card__eyebrow">Sigues jugando · Consolación</span>
        <strong className="pdraw-cons-card__vs">
          vs {rival ? matchParticipantShortName(rival) : "Por definir"} · {next.round_label}
        </strong>
        <span className="pdraw-cons-card__when">{when || SCHEDULE_PENDING_LABEL}</span>
      </section>
    );
  }

  function renderSectionBody(section: PlayerDrawSection) {
    const hasBracket = section.bracket.rounds.length > 0;
    if (section.groups.length === 0) {
      // Eliminatoria (o escalera): solo el cuadro.
      return <div className="player-draw-section__body">{hasBracket ? renderBracketCard(section, myPath, "Cuadro") : null}</div>;
    }
    const others = section.groups.filter((group) => group !== focusGroup);
    return (
      <div className="player-draw-section__body">
        {/* Eduardo 2026-10-07: ya generados, los playoffs van ARRIBA de los
            grupos (es lo que se está jugando). */}
        {hasBracket ? renderBracketCard(section, myPath, "Playoffs") : null}
        {focusGroup ? (
          <>
            {renderStandingsList(focusGroup, focusStandings)}
            {renderCrossCard(focusGroup, focusStandings, true)}
            {renderLegend(focusStandings)}
            {renderRol(focusGroup)}
          </>
        ) : null}
        {others.length > 0 ? (
          <section className="ps-card pdraw-others" aria-label="Otros grupos">
            <button
              type="button"
              className="pdraw-others__toggle"
              aria-expanded={others.some(groupIsOpen)}
              onClick={() => {
                const open = !others.some(groupIsOpen);
                setGroupOpenOverrides((prev) => ({ ...prev, ...Object.fromEntries(others.map((group) => [group.key, open])) }));
              }}
            >
              <span>
                {myGroup ? "Otros grupos" : "Más grupos"} de {section.name} · {others.map((group) => group.name.replace(/^Grupo\s+/i, "")).join(", ").replace(/, ([^,]*)$/, " y $1")}
              </span>
              <span aria-hidden="true">{others.some(groupIsOpen) ? "▴" : "▾"}</span>
            </button>
            {others.some(groupIsOpen)
              ? others.map((group) => (
                  <div key={group.key} className="pdraw-others__group">
                    <PlayerRrPosterTable
                      group={group}
                      myParticipantId={myParticipantId}
                      standings={standingsByGroupId.get(group.key) ?? null}
                      qualifyCount={standingsByGroupId.get(group.key)?.qualify_count ?? null}
                    />
                  </div>
                ))
              : null}
          </section>
        ) : null}
        {!hasBracket ? renderPlayoffsPlaceholder(section) : null}
      </div>
    );
  }

  // Sorteo (maqueta 2026-10-06): solo el de tu categoría; sin categoría
  // tuya, la franja de siempre con todas.
  const myCeremony =
    myDivisionKey != null
      ? drawCeremonies.find((ceremony) => String(ceremony.division_id ?? "general") === myDivisionKey) ?? null
      : null;
  const sorteoHref = `/player/tournaments/${tournamentId}/sorteo`;

  function renderSorteo() {
    if (loading || drawCeremonies.length === 0) return null;
    if (myCeremony) {
      const status = myCeremony.performed_at
        ? `Se hizo el ${cdmxDayMonthEs(myCeremony.performed_at, { withTime: true })} · mira cómo salió.`
        : myCeremony.scheduled_at
          ? isPastCdmx(myCeremony.scheduled_at)
            ? "Resultado pendiente de publicarse"
            : `Programado: ${cdmxDayMonthEs(myCeremony.scheduled_at, { withTime: true })}`
          : "Pendiente de programar";
      return (
        <section className="ps-card ps-sorteo" aria-label="Sorteo de tu categoría">
          <div className="ps-sorteo__text">
            <strong>Sorteo de tu categoría</strong>
            <span>{status}</span>
          </div>
          {myCeremony.performed_at ? (
            <Link className="ps-sorteo__link" to={sorteoHref}>
              Ver sorteo
            </Link>
          ) : null}
        </section>
      );
    }
    // El Draw es lo primario (feedback Eduardo 2026-07-15): del sorteo
    // solo una franja compacta; el acta completa vive en su propia página.
    return (
      <section className="card draw-ceremony draw-ceremony--strip" aria-label="Sorteo">
        <div className="draw-ceremony__strip-row">
          <div className="draw-ceremony__strip-lines">
            <strong>{drawCeremonies.some((ceremony) => ceremony.method === "rr_groups") ? "Sorteo" : "Sorteo del cuadro"}</strong>
            {drawCeremonies.map((ceremony) => {
              const divisionName =
                ceremony.division_id == null
                  ? "Torneo"
                  : matches.find((match) => match.division_id === ceremony.division_id)?.division_name?.trim() || `Categoría ${ceremony.division_id}`;
              const status = ceremony.performed_at
                ? `realizado el ${formatDateTime(ceremony.performed_at)} h (CDMX)`
                : ceremony.scheduled_at
                  ? isPastCdmx(ceremony.scheduled_at)
                    ? "resultado pendiente de publicarse"
                    : `programado para el ${formatDateTime(ceremony.scheduled_at)} h (CDMX)`
                  : "pendiente de programar";
              return (
                <span key={ceremony.id} className="muted">
                  {divisionName}: {status}
                </span>
              );
            })}
          </div>
          {drawCeremonies.some((ceremony) => ceremony.performed_at) ? (
            <Link className="btn btn--small" to={sorteoHref}>
              🎲 Resultados Sorteo
            </Link>
          ) : null}
        </div>
      </section>
    );
  }

  const locked = playerStageLocked(visibility);
  const ready = !locked && !loading && sections.length > 0 && !!selectedSection;
  const mineCard =
    ready && selectedSection
      ? myGroup
        ? renderMyGroupCard(selectedSection, myGroup)
        : myPath
          ? renderMyPathCard(selectedSection, myPath)
          : null
      : null;

  return (
    <div className="pt-reto-theme ps-page pdraw-page">
      {/* Secciones del torneo (Eduardo 2026-10-05): en computadora, pestañas
          aquí arriba; en celular, la barra flotante de abajo. */}
      <PlayerTournamentBottomNav tournamentId={tournamentId} />
      <PlayerProfileScopeSwitcher scope={scope} tournament />
      <section className="ps-card pdraw-head">
        <div className="pdraw-head__row">
          <div className="pdraw-head__text">
            <span className="ps-head__eyebrow">Draw · {tournament?.name ?? "Torneo"}</span>
            <h1>{ready && selectedSection ? selectedSection.name : "Draw"}</h1>
            {ready && selectedSection ? <span className="pdraw-head__sub">{sectionSubtitle(selectedSection)}</span> : null}
          </div>
          <div className="pdraw-head__actions">
            {ready && selectedSection && selectedSection.bracket.rounds.length > 0 ? (
              <button type="button" className="pdraw-btn" onClick={() => printBracket(selectedSection)}>
                PDF / imprimir
              </button>
            ) : null}
            <Link className="pdraw-btn" to="/player/tournaments">
              ‹ Mis torneos
            </Link>
          </div>
        </div>
        {ready && sections.length > 1 ? (
          <div className="ps-chips pdraw-chips" role="group" aria-label="Categoría">
            {sections.map((section) => {
              const active = section.key === selectedSection?.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  className={`ps-chip-button${active ? " is-active" : ""}`}
                  aria-pressed={active}
                  onClick={() => {
                    setPickedSectionKey(section.key);
                    setShowAllRol(false);
                  }}
                >
                  {section.name}
                  {section.key === myDivisionKey ? <span className="pdraw-chip-tag"> · tuya</span> : ""}
                </button>
              );
            })}
          </div>
        ) : null}
      </section>

      {error && <div className="alert alert--error">{error}</div>}

      {locked ? <PlayerStageLockedCard tournamentId={tournamentId} section="draw" /> : null}

      {mineCard}

      {ready && selectedSection && selectedSection.consolation.rounds.length > 0 ? renderConsolationCard() : null}

      {renderSorteo()}

      {locked ? null : loading ? (
        <section className="card">
          <p className="muted">Cargando bracket…</p>
        </section>
      ) : sections.length === 0 || !selectedSection ? (
        <section className="card">
          <p className="muted">
            El cuadro eliminatorio todavía no se genera. Vuelve cuando el organizador publique los playoffs.
          </p>
        </section>
      ) : (
        <div
          key={selectedSection.key}
          className={`player-draw-section${sections.length === 1 ? " player-draw-section--single" : ""}`}
        >
          {renderSectionBody(selectedSection)}
        </div>
      )}

      <TournamentSponsorSlots />
      {ready && selectedSection && focusGroup ? (
        <section className="pdraw-poster" aria-label="Póster de tu grupo">
          <span>Póster de {myGroup ? "tu grupo" : focusGroup.name}, como el que pega el club</span>
          <button type="button" className="pdraw-poster__btn" onClick={() => void downloadPoster(selectedSection, focusGroup)}>
            Descargar póster
          </button>
        </section>
      ) : null}
    </div>
  );
}

